/*
 * Optimized critical utilities
 * Keeps same functionality while reducing forced reflows and repeated layout reads.
 */
export class DeclarativeShadowElement extends HTMLElement {
  connectedCallback() {
    if (this.shadowRoot) return;

    const template = this.querySelector(':scope > template[shadowrootmode="open"]');
    if (!(template instanceof HTMLTemplateElement)) return;

    const shadow = this.attachShadow({ mode: 'open' });
    shadow.append(template.content.cloneNode(true));
  }
}

export class ResizeNotifier extends ResizeObserver {
  #initialized = false;

  constructor(callback) {
    super((entries) => {
      if (this.#initialized) return callback(entries, this);
      this.#initialized = true;
    });
  }

  disconnect() {
    this.#initialized = false;
    super.disconnect();
  }
}

export class OverflowMinimumEvent extends Event {
  constructor(minimumReached) {
    super('overflowMinimum', { bubbles: true });
    this.detail = { minimumReached };
  }
}

export class ReflowEvent extends Event {
  constructor(lastVisibleElement) {
    super('reflow', { bubbles: true });
    this.detail = { lastVisibleElement };
  }
}

export class OverflowList extends DeclarativeShadowElement {
  static get observedAttributes() {
    return ['disabled', 'minimum-items'];
  }

  #refs;
  #scheduled = false;
  #initialized = false;

  #lastDimensions = {
    width: null,
    height: null,
  };

  #resizeObserver = new ResizeNotifier((entries) => this.#handleChange(entries));
  #mutationObserver = new MutationObserver((entries) => this.#handleChange(entries));

  attributeChangedCallback(name, oldValue, newValue) {
    if (oldValue === newValue) return;

    if (name === 'disabled') {
      if (newValue === 'true') {
        this.#reset();
      } else if (this.#initialized) {
        this.#scheduleReflow();
      }
    }
  }

  connectedCallback() {
    super.connectedCallback();

    const init = () => {
      if (this.#initialized) return;
      this.#initialize();
    };

    if (this.hasAttribute('defer')) {
      const deferredReflow = () => {
        this.removeAttribute('defer');
        init();
      };

      this.#requestIdle(() => this.schedule(deferredReflow));
      return;
    }

    if (this.shadowRoot) {
      init();
      return;
    }

    requestAnimationFrame(init);
  }

  disconnectedCallback() {
    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();
    this.#initialized = false;
  }

  get schedule() {
    return typeof window.Theme?.utilities?.scheduler?.schedule === 'function'
      ? window.Theme.utilities.scheduler.schedule
      : (callback) => requestAnimationFrame(callback);
  }

  get minimumItems() {
    const value = this.getAttribute('minimum-items');
    return value ? parseInt(value, 10) : null;
  }

  get overflowSlot() {
    return this.#refs?.overflowSlot;
  }

  get defaultSlot() {
    return this.#refs?.defaultSlot;
  }

  #requestIdle(callback) {
    if (typeof window.requestIdleCallback === 'function') {
      window.requestIdleCallback(callback);
    } else {
      window.setTimeout(callback, 1);
    }
  }

  #initialize() {
    const { shadowRoot } = this;
    if (!shadowRoot) return;

    const defaultSlot = shadowRoot.querySelector('slot:not([name])');
    const overflowSlot = shadowRoot.querySelector('slot[name="overflow"]');
    const moreSlot = shadowRoot.querySelector('slot[name="more"]');
    const overflow = shadowRoot.querySelector('[part="overflow"]');
    const list = shadowRoot.querySelector('[part="list"]');
    const placeholder = shadowRoot.querySelector('[part="placeholder"]');

    if (
      !(defaultSlot instanceof HTMLSlotElement) ||
      !(overflowSlot instanceof HTMLSlotElement) ||
      !(moreSlot instanceof HTMLSlotElement) ||
      !(overflow instanceof HTMLElement) ||
      !(list instanceof HTMLUListElement) ||
      !(placeholder instanceof HTMLLIElement)
    ) {
      return;
    }

    this.#refs = {
      defaultSlot,
      overflowSlot,
      moreSlot,
      overflow,
      list,
      placeholder,
    };

    this.addEventListener('reflow', (event) => {
      this.#scheduleReflow(event.detail?.lastVisibleElement);
    });

    this.#initialized = true;
    this.#scheduleReflow();
  }

  #handleChange(entries) {
    if (this.#scheduled || !this.#initialized) return;

    const firstEntry = entries && entries[0];

    if (firstEntry instanceof ResizeObserverEntry) {
      const width = Math.round(firstEntry.contentRect.width);
      const height = Math.round(firstEntry.contentRect.height);

      if (!width || !height) return;

      if (width === this.#lastDimensions.width && height === this.#lastDimensions.height) {
        return;
      }

      this.#lastDimensions = { width, height };
    }

    this.#scheduleReflow();
  }

  #scheduleReflow(lastVisibleElement) {
    if (this.#scheduled || !this.#initialized) return;

    this.#scheduled = true;

    this.schedule(() => {
      this.#reflowItems(lastVisibleElement);
      this.#scheduled = false;
    });
  }

  #moveItemsToDefaultSlot() {
    const { defaultSlot, overflowSlot } = this.#refs;

    for (const element of overflowSlot.assignedElements()) {
      element.slot = defaultSlot.name;
    }
  }

  #reset() {
    if (!this.#refs) return;

    const { list } = this.#refs;

    this.#mutationObserver.disconnect();
    this.#resizeObserver.disconnect();
    this.#moveItemsToDefaultSlot();

    list.style.removeProperty('height');
    list.style.removeProperty('overflow');
    this.style.setProperty('--overflow-count', '0');
  }

  #updateMinimumReached(visibleElements) {
    if (this.minimumItems === null) return;

    const minimumReached = visibleElements.length < this.minimumItems;

    this.toggleAttribute('minimum-reached', minimumReached);
    this.dispatchEvent(new OverflowMinimumEvent(minimumReached));
  }

  showAll() {
    if (!this.#refs) return;

    const { placeholder } = this.#refs;

    placeholder.style.setProperty('width', '0');
    placeholder.style.setProperty('display', 'none');
    this.setAttribute('disabled', 'true');
  }

  #reflowItems(lastVisibleElement) {
    if (!this.#refs || this.getAttribute('disabled') === 'true') return;

    const { defaultSlot, overflowSlot, moreSlot, list, placeholder } = this.#refs;

    this.#resizeObserver.disconnect();
    this.#mutationObserver.disconnect();

    const firstChild = this.firstElementChild;
    const height = firstChild instanceof HTMLElement ? firstChild.offsetHeight : 0;

    if (height) list.style.height = `${height}px`;
    list.style.setProperty('overflow', 'hidden');

    this.#moveItemsToDefaultSlot();

    const elements = defaultSlot.assignedElements();
    if (!elements.length) {
      list.style.removeProperty('overflow');
      list.style.removeProperty('height');
      this.#resizeObserver.observe(this);
      this.#mutationObserver.observe(this, { childList: true });
      return;
    }

    moreSlot.hidden = true;
    placeholder.hidden = true;

    const rootTop = list.offsetTop;

    const getVisibleElements = () => {
      return elements.filter((element) => {
        if (!(element instanceof HTMLElement)) return false;
        return element.offsetTop <= rootTop;
      });
    };

    let visibleElements = getVisibleElements();

    if (visibleElements.length !== elements.length || lastVisibleElement) {
      moreSlot.style.setProperty('order', '-1');
      moreSlot.hidden = false;

      if (height) moreSlot.style.setProperty('height', `${height}px`);

      if (lastVisibleElement) {
        lastVisibleElement.style.setProperty('order', '-1');
      }

      visibleElements = getVisibleElements();

      moreSlot.style.removeProperty('order');
      moreSlot.style.removeProperty('height');

      if (lastVisibleElement) {
        lastVisibleElement.style.removeProperty('order');

        const visibleIndex = visibleElements.indexOf(lastVisibleElement);
        if (visibleIndex !== -1) {
          visibleElements.splice(visibleIndex, 1);
          visibleElements.push(lastVisibleElement);
        }
      }
    }

    const visibleSet = new Set(visibleElements);
    const overflowingElements = elements.filter((element) => !visibleSet.has(element));
    const hasOverflow = overflowingElements.length > 0;

    const firstOverflowingElement = overflowingElements[0];
    const placeholderWidth =
      firstOverflowingElement instanceof HTMLElement ? firstOverflowingElement.offsetWidth : 0;

    const overflowSet = new Set(overflowingElements);

    for (const element of elements) {
      element.slot = overflowSet.has(element) ? overflowSlot.name : defaultSlot.name;
    }

    moreSlot.hidden = !hasOverflow;

    if (hasOverflow) {
      placeholder.style.width = `${placeholderWidth}px`;
      placeholder.hidden = false;
    } else {
      placeholder.style.width = '0';
      placeholder.hidden = true;
    }

    list.style.setProperty('counter-reset', `overflow-count ${overflowingElements.length}`);
    this.style.setProperty('--overflow-count', `${overflowingElements.length}`);
    list.style.removeProperty('overflow');

    if (hasOverflow) {
      this.#updateMinimumReached(visibleElements);
    } else {
      this.removeAttribute('minimum-reached');
    }

    this.#resizeObserver.observe(this);
    this.#mutationObserver.observe(this, { childList: true });
  }
}

if (!customElements.get('overflow-list')) {
  customElements.define('overflow-list', OverflowList);
}

export function calculateHeaderGroupHeight(
  header = document.querySelector('#header-component'),
  headerGroup = document.querySelector('#header-group')
) {
  if (!headerGroup) return 0;

  let totalHeight = 0;
  const children = headerGroup.children;

  for (let i = 0; i < children.length; i++) {
    const element = children[i];

    if (element === header || !(element instanceof HTMLElement)) continue;

    totalHeight += element.offsetHeight;
  }

  if (header instanceof HTMLElement && header.hasAttribute('transparent') && header.parentElement?.nextElementSibling) {
    return totalHeight + header.offsetHeight;
  }

  return totalHeight;
}

function updateHeaderHeights() {
  const header = document.querySelector('header-component');

  if (!(header instanceof HTMLElement)) return;

  const headerHeight = header.offsetHeight;
  const headerGroupHeight = calculateHeaderGroupHeight(header);

  document.body.style.setProperty('--header-height', `${headerHeight}px`);
  document.body.style.setProperty('--header-group-height', `${headerGroupHeight}px`);
}

function updateTransparentHeaderOffset() {
  const header = document.querySelector('#header-component');
  const headerGroup = document.querySelector('#header-group');
  const hasHeaderSection = headerGroup?.querySelector('.header-section');

  if (!hasHeaderSection || !header?.hasAttribute('transparent')) {
    document.body.style.setProperty('--transparent-header-offset-boolean', '0');
    return;
  }

  const hasImmediateSection = hasHeaderSection.nextElementSibling?.classList.contains('shopify-section');
  const shouldApplyOffset = !hasImmediateSection ? '1' : '0';

  document.body.style.setProperty('--transparent-header-offset-boolean', shouldApplyOffset);
}

export function updateAllHeaderCustomProperties() {
  requestAnimationFrame(() => {
    updateHeaderHeights();
    updateTransparentHeaderOffset();
  });
}

updateAllHeaderCustomProperties();